/**
 * PlatformSuperAdmins — manage the roster of platform Super Admins.
 * ADR-WF-1 v2.1 §37 — Platform Console.
 *
 * Capabilities:
 *   • List all Super Admins (marks the current session with an "You" chip).
 *   • Create a new Super Admin (name, email, password ≥12 chars).
 *   • Reset another Super Admin's password (forces change on next login).
 *   • Delete a Super Admin (blocked for self + the last remaining admin).
 */
import { useEffect, useState } from "react";
import {
  Plus, ShieldCheck, KeyRound, Trash2, AlertTriangle, Loader2, Copy, Check,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join(", ");
  return e?.message || "Something went wrong";
}

function genPassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%^&*";
  const all = upper + lower + digits + symbols;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  let p = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
  for (let i = 0; i < 12; i++) p += pick(all);
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

export default function PlatformSuperAdmins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Create
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetting, setResetting] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/platform/v1/admins");
      setAdmins(data || []);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ name: "", email: "", password: genPassword() });
    setCopied(false);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 12) {
      return toast.error("Provide a name, email, and a password ≥ 12 characters");
    }
    setCreating(true);
    try {
      await api.post("/platform/v1/admins", form);
      toast.success(`Super Admin ${form.email} created`);
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setCreating(false);
    }
  };

  const openReset = (a) => {
    setResetTarget(a);
    setResetPwd(genPassword());
  };

  const submitReset = async () => {
    if (!resetTarget || resetPwd.length < 12) {
      return toast.error("Password must be ≥ 12 characters");
    }
    setResetting(true);
    try {
      await api.post(`/platform/v1/admins/${resetTarget.id}/reset-password`, {
        new_password: resetPwd,
      });
      toast.success(`Password reset for ${resetTarget.email}. Share the new password securely — they will be asked to change it on next login.`);
      setResetTarget(null);
      setResetPwd("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setResetting(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/platform/v1/admins/${deleteTarget.id}`);
      toast.success(`Super Admin ${deleteTarget.email} removed`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setDeleting(false);
    }
  };

  const copyPwd = async (pwd) => {
    try {
      await navigator.clipboard.writeText(pwd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <div className="space-y-8" data-testid="platform-admins-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
            Platform
          </div>
          <h1 className="text-3xl font-semibold text-slate-900 mt-1">Super Admins</h1>
          <p className="text-slate-600 mt-2 text-sm">
            Anyone in this list can access the Platform Console and every hotel on it.
            Treat this roster like a keyring.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="admins-create-btn"
          className="bg-amber-500 text-white hover:bg-amber-600 font-semibold shadow-sm"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Super Admin
        </Button>
      </header>

      {err && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 p-4 text-rose-800 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{err}</span>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm">
        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_120px_180px] items-center px-6 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold bg-slate-50">
          <div>Name</div>
          <div>Email</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-6 text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : admins.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            No super admins found.
          </div>
        ) : (
          admins.map((a) => (
            <div
              key={a.id}
              data-testid={`admin-row-${a.id}`}
              className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1.6fr)_120px_180px] items-center px-6 py-4 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-slate-900 font-medium truncate">{a.name}</div>
                  {a.is_self && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold uppercase tracking-wide">
                      You
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mono truncate">{a.id.slice(-10)}</div>
              </div>
              <div className="text-sm text-slate-700 truncate">{a.email}</div>
              <div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-semibold ${
                  a.is_active
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-100 text-slate-500 border-slate-300"
                }`}>
                  <ShieldCheck className="w-3 h-3" />
                  {a.is_active ? "Active" : "Disabled"}
                </span>
              </div>
              <div className="flex items-center justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openReset(a)}
                  data-testid={`admin-reset-${a.id}`}
                  className="h-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  title="Reset password"
                >
                  <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteTarget(a)}
                  disabled={a.is_self}
                  data-testid={`admin-delete-${a.id}`}
                  className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent"
                  title={a.is_self ? "You cannot delete your own account" : "Delete super admin"}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900">New Super Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-white border-slate-300 mt-1.5"
                data-testid="admin-name-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-white border-slate-300 mt-1.5"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Initial password (≥ 12 chars)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-white border-slate-300 mono text-[13px]"
                  data-testid="admin-password-input"
                />
                <Button
                  variant="outline"
                  onClick={() => setForm({ ...form, password: genPassword() })}
                  className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                  data-testid="admin-password-regen"
                >
                  Regen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyPwd(form.password)}
                  className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                  data-testid="admin-password-copy"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Share this password securely. Email-invite flow is on the roadmap.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              disabled={creating}
              data-testid="admin-create-submit"
              className="bg-amber-500 text-white hover:bg-amber-600 font-semibold"
            >
              {creating && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Reset password</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">
              You are resetting the password for{" "}
              <span className="text-slate-900 font-medium">{resetTarget?.email}</span>. They will be
              forced to change it on their next login.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">New password (≥ 12 chars)</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  value={resetPwd}
                  onChange={(e) => setResetPwd(e.target.value)}
                  className="bg-white border-slate-300 mono text-[13px]"
                  data-testid="admin-reset-password-input"
                />
                <Button
                  variant="outline"
                  onClick={() => setResetPwd(genPassword())}
                  className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                >
                  Regen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => copyPwd(resetPwd)}
                  className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                  data-testid="admin-reset-password-copy"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetTarget(null)}
              className="border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReset}
              disabled={resetting}
              data-testid="admin-reset-submit"
              className="bg-amber-500 text-white hover:bg-amber-600 font-semibold"
            >
              {resetting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white border-slate-200 text-slate-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Delete Super Admin?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-700">
              <span className="text-slate-900 font-medium">{deleteTarget?.email}</span> will lose all
              platform access immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-slate-300 hover:bg-slate-50 text-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              disabled={deleting}
              data-testid="admin-delete-confirm"
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
